import type { SetStateAction, WritableAtom } from 'jotai';
import { useAtom } from 'jotai';
import type { RESET } from 'jotai/utils';
import { atomFamily, atomWithStorage } from 'jotai/utils';
import type { z } from 'zod/v4';

/* -------------------------------------------------------------------------------------------------
 * Validated storage
 *
 * A single, generic way to read and write values to the browser's local or session storage. Every
 * read is parsed and validated against a zod schema, falling back to the supplied initial value when
 * the stored data is missing, unparseable or fails validation — so corrupt or outdated storage can
 * never crash the app or feed it malformed state.
 *
 * - `createValidatedStorage` builds a jotai-compatible storage adapter. Its `getItem`/`setItem`/
 *   `removeItem` methods can also be used imperatively (without jotai) for non-reactive access.
 * - `useValidatedStorage` is the reactive hook built on top of the adapter.
 * -----------------------------------------------------------------------------------------------*/

export type StorageArea = 'local' | 'session';

export interface ValidatedStorageOptions<T> {
  /**
   * Serializes the value before it is written, for values that are not plain JSON (e.g. Temporal
   * objects). Reads are always validated by the schema, so `serialize` only affects writes.
   */
  serialize?: (value: T) => unknown;
}

/**
 * Structurally compatible with jotai's `SyncStorage`, so an adapter can be passed straight to
 * `atomWithStorage` while also being usable imperatively.
 */
export interface ValidatedStorage<T> {
  getItem: (key: string, initialValue: T) => T;
  setItem: (key: string, value: T) => void;
  removeItem: (key: string) => void;
  subscribe?: (key: string, callback: (value: T) => void, initialValue: T) => () => void;
}

function getStorage(area: StorageArea): Storage {
  return area === 'local' ? localStorage : sessionStorage;
}

export function createValidatedStorage<T>(
  area: StorageArea,
  schema: z.ZodType<T>,
  options: ValidatedStorageOptions<T> = {},
): ValidatedStorage<T> {
  const { serialize } = options;

  const read = (key: string, initialValue: T): T => {
    const stored = getStorage(area).getItem(key);

    if (stored === null) {
      return initialValue;
    }

    try {
      const result = schema.safeParse(JSON.parse(stored));

      return result.success ? result.data : initialValue;
    } catch {
      return initialValue;
    }
  };

  return {
    getItem: (key, initialValue) => {
      if (typeof window === 'undefined') {
        return initialValue;
      }

      return read(key, initialValue);
    },
    setItem: (key, value) => {
      if (typeof window === 'undefined') {
        return;
      }

      getStorage(area).setItem(key, JSON.stringify(serialize ? serialize(value) : value));
    },
    removeItem: (key) => {
      if (typeof window === 'undefined') {
        return;
      }

      getStorage(area).removeItem(key);
    },
    // Only localStorage is shared across tabs, so cross-tab sync is only relevant there.
    ...(area === 'local'
      ? {
          subscribe: (key: string, callback: (value: T) => void, initialValue: T) => {
            if (typeof window === 'undefined') {
              return () => {};
            }

            const handleStorageChange = (event: StorageEvent) => {
              if (event.key === key) {
                callback(event.newValue ? read(key, initialValue) : initialValue);
              }
            };

            window.addEventListener('storage', handleStorageChange);

            return () => window.removeEventListener('storage', handleStorageChange);
          },
        }
      : {}),
  };
}

/* -------------------------------------------------------------------------------------------------
 * Reactive hook
 * -----------------------------------------------------------------------------------------------*/

type StorageAtom<T> = ReturnType<typeof atomWithStorage<T>>;
type StorageAtomFamily<T> = (key: string) => StorageAtom<T>;

/**
 * Atoms are cached per storage adapter so every component addressing the same logical store shares
 * state. A `createValidatedStorage` adapter is meant to be created once at module scope, so keying
 * the cache on it yields exactly one `atomFamily` per store.
 */
const atomFamilyByStorage = new WeakMap<object, StorageAtomFamily<unknown>>();
const allAtomFamilies: Array<ReturnType<typeof atomFamily>> = [];

function getStorageAtomFamily<T>(storage: ValidatedStorage<T>, initialValue: T): StorageAtomFamily<T> {
  const cached = atomFamilyByStorage.get(storage);

  if (cached) {
    return cached as StorageAtomFamily<T>;
  }

  const family = atomFamily((key: string) => atomWithStorage<T>(key, initialValue, storage, { getOnInit: true }));
  atomFamilyByStorage.set(storage, family as StorageAtomFamily<unknown>);
  allAtomFamilies.push(family as ReturnType<typeof atomFamily>);

  return family;
}

/**
 * Test support only: drops every cached storage atom so the next use re-reads the (freshly cleared)
 * browser storage. Atoms capture their storage value when they are created, so without this a test
 * that seeds storage before rendering would see a stale value captured by an earlier test.
 */
export function resetValidatedStorageAtoms() {
  for (const family of allAtomFamilies) {
    family.setShouldRemove(() => true);
    family.setShouldRemove(null);
  }
}

/**
 * The atom holding a validated storage adapter's value under `key`, for code that needs the atom itself —
 * to read it from the store outside a render, or to share it between `useAtomValue` and `useSetAtom`.
 */
export function getValidatedStorageAtom<T>(
  storage: ValidatedStorage<T>,
  key: string,
  initialValue: T,
): WritableAtom<T, [SetStateAction<T> | typeof RESET], void> {
  return getStorageAtomFamily(storage, initialValue)(key);
}

/**
 * Reactive `[value, setValue]` access to a validated storage adapter, keyed by `key`. Mirrors
 * `useAtom`: the value is read on init and kept in sync, and `setValue` accepts a value or updater.
 */
export function useValidatedStorage<T>(storage: ValidatedStorage<T>, key: string, initialValue: T) {
  return useAtom(getValidatedStorageAtom(storage, key, initialValue));
}
